import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatCell } from "../lib/format";
import type { ReportColumn, SortState } from "../types";

interface ReportTableProps {
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  sort?: SortState;
  onSort?: (sortKey: string) => void;
  /** Extra class for a row, e.g. to flag danger rows. */
  rowClassName?: (row: Record<string, unknown>) => string | undefined;
  emptyMessage?: string;
}

/** Shared sortable report table. Sorting is server-side: clicking a sortable
 * header calls `onSort` with the column's server sort key. */
export function ReportTable({
  columns,
  rows,
  sort,
  onSort,
  rowClassName,
  emptyMessage = "No rows match these filters.",
}: ReportTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/40">
          {columns.map((col) => {
            const sortable = !!col.sortKey && !!onSort;
            const active = sort?.sort === col.sortKey;
            return (
              <TableHead key={col.key} className={col.align === "right" ? "text-right" : undefined}>
                {sortable ? (
                  <button
                    type="button"
                    className={cn(
                      "inline-flex items-center gap-1 font-medium",
                      col.align === "right" && "flex-row-reverse",
                    )}
                    onClick={() => onSort?.(col.sortKey!)}
                  >
                    {col.label}
                    {active ? (
                      sort?.direction === "asc" ? (
                        <ArrowUp className="size-3" />
                      ) : (
                        <ArrowDown className="size-3" />
                      )
                    ) : (
                      <ArrowUpDown className="size-3 opacity-50" />
                    )}
                  </button>
                ) : (
                  col.label
                )}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i} className={rowClassName?.(row)}>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                className={cn(col.align === "right" && "text-right tabular-nums")}
              >
                {formatCell(row[col.key], col.kind)}
              </TableCell>
            ))}
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
